import { OrgConfig } from "@closerplatform/spinner-openapi";
import { Config } from "@fidget/common/config/config";
import { UrlTagsProactiveMessages } from "@fidget/common/models/url-tags-proactive-messages";
import { WindowRequestUserDetails } from "@fidget/common/models/window-requests-user-details";
import { AuthorizationParameters } from "@fidget/common/models/AuthorizationParameters";
import { IBreakingPointsInfo } from "@fidget/common/models/breakingPoints";
import { BrowserSizeInfo, IBrowserWindowSize } from "@fidget/common/models/browserWindowSize";
import { CurrentPage } from "@fidget/common/models/currentPage";
import { IncomingMessage } from "@fidget/common/models/incomingMessage";
import { PickerWidgetMessages } from "@fidget/common/models/pickerWidgetMessages";
import { UserAuthorizationResult } from "@fidget/common/models/userAuthorizationResult";
import { WindowProxy } from "@fidget/common/services/windowProxyFactory";
import { TagsChangedDetails } from "@fidget/common/models/tags-changed-details";
import { Credentials } from "@fidget/common/models/credentials";
import { ConversationStatus } from "@fidget/common/models/api-config";

export class HostWindowRequests {
  hostWindow: WindowProxy;
  static instance: HostWindowRequests;

  constructor(hostWindow: WindowProxy) {
    this.hostWindow = hostWindow;
  }

  static getInstance(hostWindow?: WindowProxy) {
    if (!HostWindowRequests.instance && hostWindow) {
      HostWindowRequests.instance = new HostWindowRequests(hostWindow);
    }
    return HostWindowRequests.instance;
  }

  maintenanceModeEnabled(message: string) {
    return this.hostWindow.sendRequest<string, void>(
      "maintenanceModeEnabled",
      message
    );
  }

  userAuthorizationSuccess(authorizationResult: UserAuthorizationResult) {
    return this.hostWindow.sendRequest<UserAuthorizationResult, void>(
      "userAuthorizationSuccess",
      authorizationResult
    );
  }

  tagsChanged(tags: TagsChangedDetails) {
    return this.hostWindow.sendRequest<TagsChangedDetails, void>("tagsChanged", tags);
  }

  maximizeWindow() {
    return this.hostWindow.sendRequest<void, void>("maximizeWidget");
  }

  userAuthorizationFailed() {
    return this.hostWindow.sendRequest<void, void>("userAuthorizationFailed");
  }

  getAuthorizationParameters(): Promise<AuthorizationParameters> {
    return this.hostWindow.sendRequest<void, AuthorizationParameters>("getAuthorizationParameters");
  }

  getLoginHintToken(): Promise<string> {
    return this.hostWindow.sendRequest<void, string>("getLoginHintToken");
  }

  widgetInitialized(success: boolean): Promise<void> {
    return this.hostWindow.sendRequest<boolean, void>("widgetInitialized", success);
  }

  sendOrgConfig(orgConfig: OrgConfig): Promise<void> {
    return this.hostWindow.sendRequest<OrgConfig, void>("orgConfig", orgConfig);
  }

  sendDeinitCommand(): Promise<void> {
    return this.hostWindow.sendRequest<void, void>("deinitCommand");
  }

  onGetUrlTagsProactiveMessages(fn: () => Promise<UrlTagsProactiveMessages>) {
    return this.hostWindow.onRequest<void, UrlTagsProactiveMessages>(
      "urlTagsProactiveMessages",
      fn
    );
  }

  sendWidgetReady(config: Config): Promise<void> {
    return this.hostWindow.sendRequest<Config, void>("widgetReady", config);
  }

  onIdentifyUser(fn: (user: WindowRequestUserDetails) => Promise<void>) {
    return this.hostWindow.onRequest<WindowRequestUserDetails, void>("identifyUser", fn);
  }

  hideWidget(): Promise<void> {
    return this.hostWindow.sendRequest<void, void>("hideWidget");
  }

  onSetSideBarMode(fn: (mode: boolean) => Promise<void>) {
    return this.hostWindow.onRequest<boolean, void>("sideBarMode", fn);
  }

  sendIncomingMessage(msg: IncomingMessage): Promise<void> {
    return this.hostWindow.sendRequest<IncomingMessage, void>("incomingMessage", msg);
  }

  onChatVisibilityChanged(
    fn: (args: { visible: boolean; initializeBeforeRender: boolean }) => Promise<void>
  ) {
    return this.hostWindow.onRequest<{ visible: boolean; initializeBeforeRender: boolean }, void>(
      "chatVisibility",
      fn
    );
  }

  reloadNeeded(): Promise<void> {
    return this.hostWindow.sendRequest<void, void>("reload");
  }

  onCurrentPageChanged(fn: (currentPage: CurrentPage) => Promise<void>) {
    return this.hostWindow.onRequest<CurrentPage, void>("currentPageChanged", fn);
  }

  onWindowResized(fn: (info: BrowserSizeInfo) => Promise<void>) {
    return this.hostWindow.onRequest<
      { breakingPoints: IBreakingPointsInfo; browserWindowSize: IBrowserWindowSize },
      void
    >("windowResized", fn);
  }

  onTabActiveChanged(fn: (isTabActive: boolean) => Promise<void>) {
    return this.hostWindow.onRequest<boolean, void>("tabActive", fn);
  }

  setUnreadMessages(isUnread: boolean): Promise<void> {
    return this.hostWindow.sendRequest<boolean, void>("isUnread", isUnread);
  }

  setIframeWidth(agentVideoExpanded: boolean): Promise<void> {
    return this.hostWindow.sendRequest<boolean, void>("agentVideoExpanded", agentVideoExpanded);
  }

  onOpenWidgetWithAutoCalling(fn: (autocall: boolean) => Promise<void>) {
    return this.hostWindow.onRequest<boolean, void>("autocall", fn);
  }

  onForgetClientRequest(fn: (reinit: boolean) => Promise<void>) {
    return this.hostWindow.onRequest<boolean, void>("forgetClient", fn);
  }

  messageSent(): Promise<void> {
    return this.hostWindow.sendRequest<undefined, void>("messageSent");
  }

  messageReceived(): Promise<void> {
    return this.hostWindow.sendRequest<undefined, void>("messageReceived");
  }

  onPickerWidgetMessagesSet(fn: (pickerWidgetMessages: PickerWidgetMessages) => Promise<void>) {
    return this.hostWindow.onRequest<PickerWidgetMessages, void>("pickerWidgetMessages", fn);
  }

  cobrowsingSessionInitialized(sessionId: string): Promise<void> {
    return this.hostWindow.sendRequest<string, void>("cobrowsingSessionInitialized", sessionId);
  }

  cobrowsingSessionEnded(): Promise<void> {
    return this.hostWindow.sendRequest<void, void>("cobrowsingSessionEnded");
  }

  onClientEndedCobrowsing(fn: () => Promise<void>) {
    return this.hostWindow.onRequest<void, void>("clientEndedCobrowsing", fn);
  }

  onUrlParams(fn: (params: { [key: string]: string }) => Promise<void>) {
    return this.hostWindow.onRequest<{ [key: string]: string }, void>("urlParams", fn);
  }

  onGetCredentials(fn: () => Promise<Credentials>) {
    return this.hostWindow.onRequest<void, Credentials>("getCredentials", fn);
  }

  agentAvatarChanged(url?: string) {
    return this.hostWindow.sendRequest<string | undefined, void>("onAgentAvatarChanged", url);
  }

  lektaContext(context: any): Promise<void> {
    return this.hostWindow.sendRequest<any, void>("onLektaContext", context);
  }

  storageGetItem(key: string): Promise<string | undefined> {
    return this.hostWindow.sendRequest<string, string | undefined>("storageGetItem", key);
  }

  storageSetItem(args: { key: string; value: string }): Promise<void> {
    return this.hostWindow.sendRequest<typeof args, void>("storageSetItem", args);
  }

  storageRemoveItem(key: string): Promise<string> {
    return this.hostWindow.sendRequest<string, string>("storageRemoveItem", key);
  }

  conversationStatusChanged(status: ConversationStatus): Promise<void> {
    return this.hostWindow.sendRequest<any, void>("conversationStatusChanged", status);
  }
}
